import {
  ColumnDef,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table"

import { rankItem } from "@tanstack/match-sorter-utils"

import { Button } from "@/components/ui/button.tsx"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table.tsx"
import { useEffect, useMemo, useState } from "preact/compat"
import { LedgerAccount } from "@/api/deduction.tsx"
import { Separator } from "@/components/ui/separator.tsx"
import { toast } from "@/components/ui/use-toast.ts"
import { ChevronDown, Columns2, Trash2, X } from "lucide-react"
import { useSearchParams } from "@/utils"
import { BACKUP_PAGE_SIZE, next_page_size, next_visible_count } from "./backup_table_paging"

// Add near the top of the file
interface TableMeta<TData> {
  updateData?: (rowIndex: number, columnId: string, value: any) => void
  onDeleteRows?: (rowIds: string[]) => void
  /** ISO code the lines' amounts are in: the deduction's own currency (dollars when absent). */
  currency?: string | null
}

// Update the DataTableProps interface
interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  meta?: TableMeta<TData>
  toolbar?: preact.ComponentChild
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // ok if the first row in the table has a value that's null, that column won't be filtered
  // https://github.com/TanStack/table/issues/4919
  // thought this was fixed by returning '' instead of null but decided to just fix it by passing empty strings to the table.
  const itemValue = row.getValue(columnId)
  if (!itemValue) return false

  const itemRank = rankItem(itemValue, value)

  // Store the itemRank info
  addMeta({
    itemRank,
  })

  // Return if the item should be filtered in/out
  return itemRank.passed
}

// Change the component to use the generic type instead of Backup
export function BackupTable<TData, TValue>(props: DataTableProps<TData, TValue>) {
  let { columns: providedColumns, data, meta, toolbar } = props
  const params = useSearchParams<{ search?: string }>()

  const [rowSelection, setRowSelection] = useState({})
  // Update default sorting to use promo_sub_type
  const [sorting, setSorting] = useState<SortingState>([{ id: "promo_type", desc: false }])
  const [globalFilter, setGlobalFilter] = useState(params.search ?? "")
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [visibleCount, setVisibleCount] = useState(BACKUP_PAGE_SIZE)

  // Start from one page again whenever the rows or the search change.
  useEffect(() => {
    setVisibleCount(BACKUP_PAGE_SIZE)
  }, [data, globalFilter])

  useEffect(() => {
    const visibleColumns: VisibilityState = {}
    providedColumns.forEach((column: any) => {
      // hack to always show these columns (mostly relevant for accounting accounting_detail page)
      if (
        column.id === "reason_code" ||
        column.accessorKey === "retailer_name" ||
        column.accessorKey === "code_description" ||
        column.accessorKey === "product_category" ||
        column.accessorKey === "execution_date" ||
        column.accessorKey === "retailer_invoice_number" ||
        column.accessorKey === "product_line"
      ) {
        visibleColumns[column.id || column.accessorKey] = true
        return
      }

      visibleColumns[column.accessorKey as string] = data.some(row => {
        const value = (row as any)[column.accessorKey as string]
        return !!value
      })
    })
    setColumnVisibility(visibleColumns)
  }, [data, providedColumns])

  const table = useMemo(() => {
    return useReactTable({
      data,
      columns: providedColumns,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      globalFilterFn: fuzzyFilter,
      onColumnVisibilityChange: setColumnVisibility,
      onGlobalFilterChange: setGlobalFilter,
      onRowSelectionChange: setRowSelection,
      onSortingChange: setSorting,
      meta: meta as TableMeta<TData>,
      state: {
        columnVisibility,
        rowSelection,
        sorting,
        globalFilter,
      },
    })
  }, [data, providedColumns, columnVisibility, globalFilter, rowSelection, sorting, meta])

  const handleBulkSelectMatch = async (code: LedgerAccount) => {
    const selectedRows = table.getSelectedRowModel().rows

    // Update the local state to reflect the changes
    selectedRows.forEach(row => {
      // Update both reason_code and expense_account
      // @ts-ignore
      if (table.options.meta?.updateData) {
        // @ts-ignore
        table.options.meta.updateData(row.index, "reason_code", code)
        // @ts-ignore
        table.options.meta.updateData(row.index, "expense_account", code.expense_account)
        // @ts-ignore
        table.options.meta.updateData(row.index, "name", code.name)
      }
    })

    toast({
      title: "Success",
      description: `${selectedRows.length} accounting match${selectedRows.length > 1 ? "es" : ""} completed`,
    })

    setRowSelection({})
  }

  const isRowSelected = Object.keys(rowSelection).length > 0
  const allRows = table.getRowModel().rows
  const shownRows = allRows.slice(0, visibleCount)
  const moreCount = next_page_size(visibleCount, allRows.length)

  return (
    <>
      {table.getRowModel().rows?.length > 0 ? (
        <div className="flex items-center justify-between py-4">
          <div className="flex gap-2">{toolbar}</div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="text-slate-500">
                <Columns2 className="h-4" />
                Columns
                <ChevronDown className="h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter(column => column.getCanHide())
                .sort((a, b) => a.id.localeCompare(b.id))
                .map(column => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value: any) => column.toggleVisibility(!!value)}>
                      {column.id.replaceAll("_", " ")}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : (
        <div className="flex gap-2 my-4">{toolbar}</div>
      )}
      <Table>
        <TableHeader className="bg-slate-50">
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                return (
                  <TableHead key={header.id} className="px-1 py-1 h-8 [&:has([role=checkbox])]:px-1">
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {shownRows.length ? (
            shownRows.map(row => {
              const isNewRow = (row.original as any)?.actor === "manual"
              return (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={`${isNewRow ? "bg-green-50" : ""}`}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      className="hover:cursor-pointer px-1 py-0 h-8 [&:has([role=checkbox])]:px-1"
                      // onClick={() => handleRowClick(row, cell.id)}
                      key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })
          ) : (
            <TableRow>
              <TableCell colSpan={providedColumns.length} className="h-24 text-center">
                No backup parsed.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {moreCount > 0 && (
        <div class="flex items-center justify-center gap-3 py-3 text-sm text-slate-500">
          <span>
            Showing {shownRows.length.toLocaleString()} of {allRows.length.toLocaleString()} lines
          </span>
          <Button
            variant="outline"
            class="text-slate-600"
            onClick={() => setVisibleCount(count => next_visible_count(count, allRows.length))}>
            Show {moreCount.toLocaleString()} more
          </Button>
        </div>
      )}

      {isRowSelected && (
        <div class="flex fixed bottom-10 z-10 bg-slate-900 rounded-lg border left-1/2 transform -translate-x-1/2 p-2">
          <div className="flex justify-start grow">
            <Button onClick={() => setRowSelection({})} className="text-gray-400" variant="link">
              {`${table.getFilteredSelectedRowModel().rows.length} selected`}
              <X className="h-4" />
            </Button>
          </div>
          <Separator orientation="vertical" className="h-10 bg-slate-700 mr-3" />
          <div className="flex justify-end space-x-3">
            <Button
              variant="destructive"
              onClick={() => {
                const selectedRows = table.getSelectedRowModel().rows
                // @ts-ignore
                const rowIds = selectedRows.map(row => row.original.id)
                // @ts-ignore
                meta?.onDeleteRows?.(rowIds)
                setRowSelection({})
              }}
              className="bg-red-600 hover:bg-red-700">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Selected
            </Button>
            {/*<AutocompleteSelect onSelect={handleBulkSelectMatch} />*/}
          </div>
        </div>
      )}
    </>
  )
}
